import { AxiosRequestConfig } from "axios";

import { ApiResponse, get, post } from "@api/httpClient";

import { GlobalFileUploadConfigResponse } from "@shared/services/fileUpload";

interface ApplicantUploadInitPayload {
  filename: string;
}

interface ApplicantUploadInitParams extends Pick<AxiosRequestConfig, "signal"> {
  applicantExamId: number;
  resultId: number;
  payload: ApplicantUploadInitPayload;
}

export type ApplicantUploadInitResponse = ApiResponse<{ uploadId: string }>;

/**
 * returns an upload id which you can use to start the multi file upload process
 */
export async function postApplicantUploadInit({
  applicantExamId,
  resultId,
  payload,
  signal,
}: ApplicantUploadInitParams) {
  const { data } = await post<ApplicantUploadInitResponse>(
    `/api/applicants/exams/${applicantExamId}/results/${resultId}/localfiles/upload/initialize`,
    {
      signal,
      data: payload,
    },
  );

  return data;
}

interface ApplicantUploadPresignedPayload {
  contentLengthBytes: number;
  filename: string; // "example.zip"
  uploadId: string;
  partNumber: number;
}

interface ApplicantUploadPresignedParams
  extends Pick<AxiosRequestConfig, "signal"> {
  applicantExamId: number;
  resultId: number;
  payload: ApplicantUploadPresignedPayload;
}

export type ApplicantUploadPresignedResponse = ApiResponse<{
  url: string;
}>;

/**
 * returns a presigned url which you can upload the file chunk to
 */
export async function postApplicantUploadPresigned({
  applicantExamId,
  resultId,
  payload,
  signal,
}: ApplicantUploadPresignedParams) {
  const { data } = await post<ApplicantUploadPresignedResponse>(
    `/api/applicants/exams/${applicantExamId}/results/${resultId}/localfiles/upload/presigned`,
    {
      signal,
      data: payload,
    },
  );

  return data;
}

export interface ApplicantUploadCompletePayload {
  filename: string;
  uploadId: string;
  parts: {
    partNumber: number;
    eTag: string;
  }[];
}

interface ApplicantUploadCompleteParams
  extends Pick<AxiosRequestConfig, "signal"> {
  applicantExamId: number;
  resultId: number;
  payload: ApplicantUploadCompletePayload;
}

export type ApplicantUploadCompleteResponse = ApiResponse<"completed">;

export async function postApplicantUploadComplete({
  applicantExamId,
  resultId,
  payload,
  signal,
}: ApplicantUploadCompleteParams) {
  const { data } = await post(
    `/api/applicants/exams/${applicantExamId}/results/${resultId}/localfiles/upload/complete`,
    {
      signal,
      data: payload,
    },
  );

  return data;
}

export async function getGlobalFileUploadConfig(options?: AxiosRequestConfig) {
  const { data } = await get<GlobalFileUploadConfigResponse>(
    `/api/applicants/localfiles/upload/config`,
    options,
  );

  return data;
}
